import { getToken, setToken } from './auth';

/* ---------------------- Helpers ---------------------- */

const buildHeaders = (options = {}) => {
  const token = getToken();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
  window.location.href = '/Login';
  throw new Error('Session expired. Please log in again.');
};

const refreshTokenAndRetry = async (url, options, headers, responseHandler) => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    clearSessionAndRedirect();
  }

  try {
    const refreshRes = await fetch(
      `${window._env_.API_BASE_URL}/auth/refresh`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      }
    );

    if (!refreshRes.ok) {
      clearSessionAndRedirect();
    }

    const { access_token } = await refreshRes.json();
    setToken(access_token);
    localStorage.setItem('token', access_token);
    localStorage.setItem('access_token', access_token);

    headers.Authorization = `Bearer ${access_token}`;

    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const retryResponse = await fetch(url, { ...options, headers });

    if (!retryResponse.ok) {
      throw new Error(`HTTP error! Status: ${retryResponse.status}`);
    }

    return responseHandler(retryResponse);
  } catch (err) {
    console.error('Token refresh failed:', err);
    clearSessionAndRedirect();
  }
};

/* ---------------------- Main APIs ---------------------- */

export async function apiFetch(url, options = {}) {
  const headers = buildHeaders(options);
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    return refreshTokenAndRetry(
      url,
      options,
      headers,
      (res) => res.json()
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

export async function apiFetchBlob(url, options = {}) {
  const headers = buildHeaders(options);
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    return refreshTokenAndRetry(
      url,
      options,
      headers,
      (res) => res.blob()
    );
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.blob();
}
